import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { Client, Mountpoint } from "@/types/api";

const KEY = "clients";

export function useClients() {
  return useQuery({
    queryKey: [KEY],
    queryFn: async () => (await api.get<Client[]>("/api/clients")).data,
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, id],
    queryFn: async () => (await api.get<Client>(`/api/clients/${id}`)).data,
    enabled: !!id,
  });
}

export function useClientMountpoints(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, id, "mountpoints"],
    queryFn: async () => (await api.get<Mountpoint[]>(`/api/clients/${id}/mountpoints`)).data,
    enabled: !!id,
  });
}

export interface CreateClientInput {
  name: string;
  username: string;
  password: string;
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateClientInput) =>
      (await api.post<Client>("/api/clients", input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export interface UpdateClientInput {
  id: string;
  name?: string;
  username?: string;
  password?: string;
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: UpdateClientInput) =>
      (await api.patch<Client>(`/api/clients/${id}`, patch)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: [KEY, vars.id] });
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/clients/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useToggleClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, enable }: { id: string; enable: boolean }) =>
      (await api.post<Client>(`/api/clients/${id}/${enable ? "enable" : "disable"}`)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: [KEY, vars.id] });
    },
  });
}
